import { ACCOUNT_MENU, HOST, accountMenuRowAnchor } from '../engine/anchors';
import {
  closeHoverMenu,
  holdMenuOpen,
  openHoverMenu,
  type HoverMenu,
} from './hoverMenu';
import type { TaskFlow } from './types';

const MENU: HoverMenu = {
  name: 'account',
  trigger: HOST.accountMenuTrigger,
  panel: ACCOUNT_MENU,
};


const ensureOpen = async (): Promise<void> => {
  await openHoverMenu(MENU);
};

let releaseHold: (() => void) | null = null;

export const ACCOUNT_MENU_TASK: TaskFlow = {
  id: 'walk-account-menu',
  stepId: 'account-menu',
  cta: 'Try it: open the account menu',
  preparingLabel: 'Opening the account menu…',
  title: 'Account menu — settings and your team',
  summary: 'One stop per row, Account settings through Sign Out. Nothing is opened for you.',
  doneTitle: 'That is the account menu',
  doneDescription:
    'Everything about the workspace rather than in it: your own profile and security, the WhatsApp accounts and plan behind it, who else can get in and what they may open, and the record of everything that has changed.',

  whenDone: 'close',

  beforeStart: async () => {
    releaseHold?.();
    releaseHold = holdMenuOpen(MENU);
    if (!(await openHoverMenu(MENU))) {
      console.warn(
        '[tour-guide] "walk-account-menu": the account menu never opened. Its steps ' +
          'will resolve nothing and be skipped.',
      );
    }
  },

  afterEnd: async () => {
    releaseHold?.();
    releaseHold = null;
    await closeHoverMenu(MENU);
  },

  steps: [
    {
      id: 'account-settings',
      label: 'Settings',
      title: 'Account settings — the whole setup in one page',
      description:
        'A full page with its own left nav: your business details, password and security, which notifications you get, the WhatsApp Business accounts on this workspace, the MCP connection, your plan and your invoices. This is where a new WhatsApp number is actually connected, and where the plan is changed.',
      anchor: { selector: HOST.accountSettingsRow },
      side: 'left',
      spotlight: true,
      prepare: ensureOpen,
    },

    {
      id: 'account-team',
      label: 'Team',
      title: 'Team management — who else gets in',
      description:
        'Invite teammates, give each of them a role, and choose exactly which sections of the sidebar they can open — permissions are per person, not just per role, so an agent can be given the inbox and nothing else. It is also where a teammate is deactivated, which takes their access away without deleting what they did.',
      anchor: accountMenuRowAnchor('Team management'),
      side: 'left',
      spotlight: true,
      prepare: ensureOpen,
    },

    {
      id: 'account-audit',
      label: 'Audit log',
      title: 'Audit log — who changed what',
      description:
        'A record of every change made in this workspace: who did it, when, and from where. The first place to look when something is not as you left it — a campaign that went out, a contact that moved stage, a setting that is no longer what you set. It is a read-only history; nothing here can be edited away.',
      anchor: { selector: HOST.accountAuditLogRow },
      side: 'left',
      spotlight: true,
      prepare: ensureOpen,
    },

    {
      id: 'account-datastore',
      label: 'Data Store',
      title: 'Data Store — your own tables',
      description:
        'Spreadsheet-style tables you define yourself. Useful for anything the product does not model out of the box — inventory, referral codes, event registrations — and readable and writable from inside an automation flow.',
      anchor: accountMenuRowAnchor('Data Store'),
      side: 'left',
      spotlight: true,
      prepare: ensureOpen,
    },

    {
      id: 'account-guide',
      label: 'Guide',
      title: 'Guide — the getting-started pages',
      description:
        'Written walkthroughs of the things worth doing first, and the button that reaches our support team. This tour and that page cover the same ground from different ends: this one shows you where everything is, the Guide takes one job at a time in detail.',
      anchor: { selector: HOST.accountGuideRow },
      side: 'left',
      spotlight: true,
      prepare: ensureOpen,
    },

    {
      id: 'account-updates',
      label: 'Updates',
      title: 'Updates — what changed in the product',
      description:
        'The changelog: what we shipped, newest first. Worth a look when a screen is not quite where you remember leaving it, or when you are waiting on something that was on its way.',
      anchor: { selector: HOST.accountUpdatesRow },
      side: 'left',
      spotlight: true,
      prepare: ensureOpen,
    },

    {
      id: 'account-signout',
      label: 'Sign out',
      title: 'Sign Out — ending the session',
      description:
        'The red item below the line, kept apart from the rest so it is not pressed by accident. It signs you out and returns you to the login screen; nothing about the workspace, your team or your data changes. This is the last stop on the tour — pressing Next closes the guide rather than signing you out.',
      anchor: { selector: HOST.accountSignOutRow },
      side: 'left',
      spotlight: true,
      prepare: ensureOpen,
    },
  ],
};
